import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UniversityScoreboard } from "@/components/UniversityScoreboard";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PAYMENTS_ENABLED } from "@/config";
import {
  GraduationCap, HelpCircle, Search, User, Rocket, ChevronRight, Trophy,
  FileQuestion, FileText, PlayCircle, Bot, Building2, BookOpen, Home, Mail, Flame, Youtube, Moon, Sun,
} from "lucide-react";

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/355f71b7-7e99-4eba-a435-3b95ed879dd1/images/be821171e8ba444ba24bd3ddbe25d34ce5c235965ff958800099068c048bc783.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readJson = (k) => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } };

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, login, trialInfo } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [ads, setAds] = useState({});
  const [streak, setStreak] = useState(null);
  const lastCourse = readJson("cm_last_course");
  const lastUni = readJson("cm_last_uni");
  const recent = (readJson("cm_recent_courses") || []).slice(0, 3);
  const uniLabel = (raw) => (lastUni && raw === lastUni.id && lastUni.name) ? lastUni.name : (raw && raw.includes("_") ? "" : raw);

  useEffect(() => {
    // Country landing: ?country=JO forces redirect; otherwise IP geo (once per session)
    const GEO_SLUGS = { JO: "jordan", PS: "palestine", GE: "georgia", IT: "italy" };
    const forced = (new URLSearchParams(window.location.search).get("country") || "").toUpperCase();
    if (forced && GEO_SLUGS[forced]) {
      sessionStorage.setItem("cm_geo_country", forced);
      navigate(`/country/${GEO_SLUGS[forced]}`, { replace: true });
      return;
    }
    if (sessionStorage.getItem("cm_geo_redirect_done")) return;
    sessionStorage.setItem("cm_geo_redirect_done", "1");
    fetch(`${API}/track/geo`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const code = (d?.country || "").toUpperCase();
        if (code) sessionStorage.setItem("cm_geo_country", code);
        const slug = GEO_SLUGS[code];
        if (slug) navigate(`/country/${slug}`, { replace: true });
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) { setStreak(null); return; }
    const t = localStorage.getItem("session_token");
    fetch(`${API}/streak/me`, { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setStreak(d))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    fetch(`${API}/ads`).then((r) => r.json()).then((d) => {
      const map = {};
      (d.ads || []).forEach((a) => { map[a.slot] = a; });
      setAds(map);
    }).catch(() => {});
  }, []);

  const quickFeatures = [
    { icon: FileQuestion, label: "Practice Questions", tid: "qf-questions" },
    { icon: FileText, label: "Course Summaries", tid: "qf-summaries" },
    { icon: PlayCircle, label: "Videos", tid: "qf-videos" },
    { icon: Bot, label: "AI Study Assistant", tid: "qf-assistant" },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0B1524] pb-20 md:pb-8">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/90 dark:bg-[#0B1524]/90 backdrop-blur border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="landing-logo">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#0F2744]">
              <GraduationCap className="h-5 w-5 text-[#FF6B35]" />
            </div>
            <span className="text-xl font-extrabold text-[#1E3A5F] dark:text-white">Class<span className="text-[#2563EB] dark:text-[#7EB2FF]">Mate</span></span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => navigate("/onboarding")} className="hidden md:block px-3 py-1.5 text-sm font-semibold text-[#1E3A5F] dark:text-slate-200 hover:text-[#2563EB] transition-colors" data-testid="topbar-universities-link">
              Universities
            </button>
            <button onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })} className="hidden md:block px-3 py-1.5 text-sm font-semibold text-[#1E3A5F] dark:text-slate-200 hover:text-[#2563EB] transition-colors" data-testid="topbar-how-link">
              How It Works
            </button>
            <button onClick={() => navigate("/pricing")} className="hidden sm:block px-3 py-1.5 text-sm font-semibold text-[#1E3A5F] dark:text-slate-200 hover:text-[#2563EB] transition-colors" data-testid="topbar-pricing-link">
              Pricing
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="landing-theme-toggle" aria-label="Toggle dark mode">
              {theme === "light" ? <Moon className="h-5 w-5 text-[#1E3A5F]" /> : <Sun className="h-5 w-5 text-amber-400" />}
            </button>
            {!user && (
              <button onClick={login} className="hidden sm:block px-3 py-1.5 text-sm font-semibold text-[#1E3A5F] dark:text-slate-200 hover:text-[#2563EB] transition-colors" data-testid="topbar-signin-btn">
                Sign In
              </button>
            )}
            <button onClick={() => (user ? navigate("/my-courses") : login())} className="px-3 sm:px-4 py-1.5 rounded-full bg-[#2563EB] text-white text-xs sm:text-sm font-bold whitespace-nowrap hover:bg-[#1E40AF] transition-colors" data-testid="topbar-trial-btn">
              Start Free
            </button>
            {user && (
              <button onClick={() => navigate("/home")} className="p-2 rounded-full bg-slate-100 dark:bg-slate-800" data-testid="topbar-profile-btn" aria-label="Profile">
                <User className="h-5 w-5 text-[#1E3A5F] dark:text-slate-200" />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4">
        {/* Hero */}
        <section className="grid md:grid-cols-2 gap-8 items-center pt-10 pb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold leading-[1.15] text-[#0F2744] dark:text-white" style={{ fontFamily: "'Libre Baskerville', serif" }} data-testid="hero-title">
              Study Smarter.<br />Pass with <span className="text-[#2563EB] dark:text-[#7EB2FF]">Confidence.</span>
            </h1>
            <p className="mt-4 text-base md:text-lg text-slate-600 dark:text-slate-300 max-w-md" data-testid="hero-support-line">
              Your university courses, practice questions, AI-powered videos, exams and flashcards — all in one place.
            </p>
            <p className="mt-3 text-sm font-semibold text-[#2563EB] dark:text-[#7EB2FF]" data-testid="hero-positioning-line">
              Choose your university. Choose your course. Start studying.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => (user ? navigate("/my-courses") : login())}
                className="px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-[#1E40AF] text-white font-extrabold text-base shadow-lg shadow-blue-600/25 hover:scale-[1.02] transition-transform"
                data-testid="hero-start-free-btn"
              >
                Start Free
              </button>
              <button
                onClick={() => navigate("/onboarding")}
                className="px-8 py-3.5 rounded-xl border-2 border-slate-300 dark:border-slate-600 text-[#1E3A5F] dark:text-slate-200 font-bold text-base hover:border-[#2563EB] hover:text-[#2563EB] transition-colors"
                data-testid="hero-find-university-btn"
              >
                Find My University
              </button>
            </div>
          </div>

          {/* Product preview */}
          <div className="flex justify-center" data-testid="hero-product-preview">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 shadow-2xl shadow-[#1E3A5F]/10 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200/70 dark:border-slate-700/70 text-[11px] font-medium text-slate-500 dark:text-slate-400 truncate" data-testid="preview-breadcrumb">
                Ajman University <span className="text-slate-300">→</span> Medicine <span className="text-slate-300">→</span> Year 1 <span className="text-slate-300">→</span> <span className="text-[#1E3A5F] dark:text-slate-200 font-semibold">Human Anatomy I</span>
              </div>
              <div className="px-4 pt-3 flex gap-1.5 text-[11px] font-semibold" data-testid="preview-tabs">
                <span className="px-2.5 py-1 rounded-full bg-[#2563EB] text-white">Questions</span>
                <span className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">Exams</span>
                <span className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">Videos</span>
                <span className="px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">Flashcards</span>
              </div>
              <div className="p-4">
                <p className="text-sm font-semibold text-[#0F2744] dark:text-white leading-snug" data-testid="preview-question">
                  Which nerve innervates the deltoid muscle?
                </p>
                <div className="mt-3 space-y-1.5 text-[13px]">
                  <div className="flex items-center gap-2 rounded-lg border-2 border-[#2563EB] bg-[#2563EB]/5 px-3 py-2 text-[#1E3A5F] dark:text-slate-100 font-medium">
                    <span className="w-3.5 h-3.5 rounded-full border-[4px] border-[#2563EB] shrink-0" /> Axillary nerve
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-slate-600 dark:text-slate-300">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 shrink-0" /> Radial nerve
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-slate-600 dark:text-slate-300">
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 shrink-0" /> Musculocutaneous nerve
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-[11px] text-slate-400 mb-1">
                    <span>Question 4 of 20</span><span>20%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div className="h-full w-1/5 rounded-full bg-[#2563EB]" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Start Studying CTA */}
        <div id="how-it-works" />
        <button
          onClick={() => navigate(lastUni?.id ? "/my-courses" : "/onboarding")}
          className="w-full flex items-center gap-4 rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#1E40AF] text-white p-5 shadow-xl shadow-blue-600/25 hover:shadow-2xl hover:scale-[1.01] transition-all"
          data-testid="start-studying-cta"
        >
          <span className="flex items-center justify-center w-14 h-14 rounded-full bg-white/15 shrink-0">
            <Rocket className="h-7 w-7" />
          </span>
          <span className="text-left flex-1">
            <span className="block text-xl md:text-2xl font-bold">Start Studying</span>
          </span>
          <ChevronRight className="h-6 w-6 shrink-0" />
        </button>

        {/* Free quiz CTA (guests only) */}
        {!user && (
          <button
            onClick={() => navigate("/free-quiz")}
            className="mt-4 w-full flex items-center gap-4 rounded-2xl border-2 border-[#2563EB]/25 bg-[#2563EB]/5 hover:bg-[#2563EB]/10 dark:bg-[#2563EB]/10 dark:hover:bg-[#2563EB]/20 p-5 text-left transition-colors"
            data-testid="free-quiz-cta"
          >
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#2563EB]/15 shrink-0">
              <FileQuestion className="h-7 w-7 text-[#2563EB]" />
            </span>
            <span className="flex-1">
              <span className="block text-xl md:text-2xl font-bold text-[#0F2744] dark:text-white">Try 10 Free Questions</span>
            </span>
            <ChevronRight className="h-6 w-6 text-[#2563EB] shrink-0" />
          </button>
        )}

        {/* Pricing / trial CTA (guests only) */}
        {!user && (
          <div className="mt-4 rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#1E40AF] text-white p-6 shadow-xl shadow-blue-600/25" data-testid="pricing-teaser">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="flex-1">
                <p className="text-xl md:text-2xl font-extrabold leading-snug">Try everything free for 7 days</p>
                <p className="text-sm text-blue-100 mt-1.5">Then from <span className="font-bold text-white">$9.99/mo</span> — cancel anytime.</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => (user ? navigate("/pricing") : login())} className="px-7 py-3.5 rounded-xl bg-[#FF6B35] hover:bg-[#e85f2d] text-white font-extrabold text-base shadow-lg shadow-orange-600/30 hover:scale-[1.03] transition-transform" data-testid="trial-teaser-btn">
                  Start 7-Day Free Trial →
                </button>
                <button onClick={() => navigate("/pricing")} className="text-sm font-semibold text-blue-100 hover:text-white underline underline-offset-2" data-testid="pricing-teaser-btn">
                  Compare plans
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade banner — signed-in users without an active subscription */}
        {PAYMENTS_ENABLED && user && trialInfo?.subscriptionStatus !== "active" && (
          <div className="mt-4 rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#1E40AF] text-white p-5 shadow-xl shadow-blue-600/25 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="upgrade-banner">
            <div className="flex-1">
              <p className="text-xl md:text-2xl font-extrabold leading-snug">
                {trialInfo?.isTrialActive
                  ? `${trialInfo.daysRemaining} day${trialInfo.daysRemaining === 1 ? "" : "s"} left in your free trial`
                  : "Unlock full access to all your courses"}
              </p>
              <p className="text-sm text-blue-100 mt-1">From <span className="font-bold text-white">$9.99/mo</span> — cancel anytime.</p>
            </div>
            <button onClick={() => navigate("/pricing")} className="shrink-0 px-7 py-3.5 rounded-xl bg-[#FF6B35] hover:bg-[#e85f2d] text-white font-extrabold text-base shadow-lg shadow-orange-600/30 hover:scale-[1.03] transition-transform" data-testid="upgrade-banner-btn">
              Upgrade Now →
            </button>
          </div>
        )}

        {/* Daily study streak */}
        {user && streak && (
          <div className={`mt-4 rounded-2xl border p-4 flex items-center gap-4 shadow-sm ${streak.practiced_today ? "bg-orange-50 border-orange-200/80" : "bg-white border-slate-200/70"}`} data-testid="streak-card">
            <span className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 ${streak.practiced_today ? "bg-orange-500/15" : "bg-slate-100"}`}>
              <Flame className={`h-6 w-6 ${streak.practiced_today || streak.current_streak > 0 ? "text-orange-500" : "text-slate-400"}`} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold tracking-wide text-orange-600 uppercase">Daily study streak</p>
              <p className="text-lg font-bold text-[#0F2744] dark:text-white" data-testid="streak-count">
                {streak.current_streak === 1 ? "1 day" : `${streak.current_streak} days`} in a row
              </p>
              <p className="text-xs text-slate-500" data-testid="streak-message">
                {streak.practiced_today
                  ? "You practiced today — see you tomorrow! 🔥"
                  : streak.current_streak > 0
                    ? "You haven't practiced today — keep your streak alive!"
                    : "Answer a few questions today to start your streak!"}
              </p>
            </div>
            {!streak.practiced_today && (
              <button onClick={() => navigate(lastCourse?.id ? `/course/${lastCourse.id}` : "/home")}
                      className="px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-1 shrink-0"
                      data-testid="streak-practice-btn">
                Practice now <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Continue where you left off */}
        {lastCourse?.id && (
          <div className="mt-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/70 p-4 flex items-center gap-4 shadow-sm" data-testid="continue-card">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 shrink-0">
              <BookOpen className="h-6 w-6 text-emerald-600" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold tracking-wide text-emerald-600 uppercase">Continue where you left off</p>
              <p className="text-lg font-bold text-[#0F2744] dark:text-white truncate">{lastCourse.name}</p>
              {uniLabel(lastCourse.uni) && <p className="text-xs text-slate-500 truncate">{uniLabel(lastCourse.uni)}</p>}
            </div>
            <button onClick={() => navigate(`/course/${lastCourse.id}`)}
                    className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-1 shrink-0"
                    data-testid="continue-btn">
              Continue <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* My University */}
        {lastUni?.id && (
          <div className="mt-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/70 p-4 flex items-center gap-4 shadow-sm" data-testid="my-university-card">
            <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#2563EB]/10 shrink-0">
              <Building2 className="h-6 w-6 text-[#2563EB]" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-bold text-[#0F2744] dark:text-white">My University</p>
              <p className="text-sm text-slate-500 truncate">{lastUni.name || lastUni.id}</p>
            </div>
            <button onClick={() => navigate(`/university/${lastUni.id}`)}
                    className="px-4 py-2.5 rounded-lg border border-[#2563EB]/40 text-[#2563EB] text-sm font-semibold flex items-center gap-1 hover:bg-[#2563EB]/5 shrink-0"
                    data-testid="my-university-btn">
              Go to My Courses <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Your Courses */}
        {recent.length > 0 && (
          <section className="mt-6" data-testid="your-courses">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base md:text-lg font-bold text-[#0F2744] dark:text-white flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#2563EB]" /> Your Courses
              </h2>
              {lastUni?.id && (
                <button onClick={() => navigate(`/university/${lastUni.id}`)} className="text-sm font-semibold text-[#2563EB] flex items-center gap-0.5" data-testid="your-courses-view-all">
                  View all <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {recent.map((c) => (
                <div key={c.id} className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/70 p-4 shadow-sm flex flex-col" data-testid={`recent-course-${c.id}`}>
                  <p className="font-bold text-[#0F2744] dark:text-white leading-snug">{c.name}</p>
                  {uniLabel(c.uni) && <p className="text-xs text-slate-500 mt-0.5 truncate">{uniLabel(c.uni)}</p>}
                  <div className="mt-2 space-y-1 text-xs text-slate-600">
                    {c.q != null && <p className="flex items-center gap-1.5"><FileQuestion className="h-3.5 w-3.5 text-[#2563EB]" /> {c.q} Questions</p>}
                    {c.v != null && <p className="flex items-center gap-1.5"><PlayCircle className="h-3.5 w-3.5 text-[#2563EB]" /> {c.v} Videos</p>}
                  </div>
                  <button onClick={() => navigate(`/course/${c.id}`)}
                          className="mt-3 w-full py-2 rounded-lg bg-[#2563EB]/10 text-[#2563EB] text-sm font-semibold flex items-center justify-center gap-1 hover:bg-[#2563EB]/20"
                          data-testid={`recent-course-open-${c.id}`}>
                    Start Studying <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Championship + Top Universities (signed-in students) */}
        {user && (
        <section className="mt-6 grid md:grid-cols-5 gap-3">
          <div className="md:col-span-3 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border border-amber-200/60 dark:border-amber-800/40 p-5 flex items-center gap-4" data-testid="championship-banner">
            <div className="flex-1">
              <p className="text-lg font-bold text-[#0F2744] dark:text-white">Represent Your University 🏆</p>
              <p className="text-sm text-slate-600 mt-1">Answer questions. Earn points. Move your university up the ranking!</p>
              <button onClick={() => navigate("/championship")}
                      className="mt-3 px-5 py-2.5 rounded-lg bg-[#2563EB] hover:bg-[#1E40AF] text-white text-sm font-semibold"
                      data-testid="championship-play-btn">
                Play Now 🔥
              </button>
            </div>
            <Trophy className="h-16 w-16 text-amber-500 shrink-0" />
          </div>
          <div className="md:col-span-2 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-700/70 p-4 shadow-sm overflow-hidden" data-testid="top-universities-card">
            <UniversityScoreboard compact top={5} minimal />
          </div>
        </section>
        )}

        {/* Ad slots — render only real ads */}
        {(ads[1] || ads[2]) && (
          <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ad-slots">
            {[1, 2].map((n) =>
              ads[n] ? (
                <a key={n} href={ads[n].link_url || "#"} target="_blank" rel="noreferrer" data-testid={`ad-slot-${n}`}
                   className="block h-24 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                  <img src={ads[n].image} alt={ads[n].title || `Ad ${n}`} className="w-full h-full object-cover" />
                </a>
              ) : null
            )}
          </section>
        )}

        {/* Footer links */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
          <a href="mailto:classmate22@gmail.com" className="flex items-center gap-2 hover:text-[#FF6B35]"><Mail className="h-4 w-4" /> classmate22@gmail.com</a>
          <a href="https://youtube.com/@classmatemedical" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#FF6B35]" data-testid="youtube-channel-link"><Youtube className="h-4 w-4" /> YouTube</a>
          <button onClick={() => navigate("/ambassador")} className="flex items-center gap-2 hover:text-[#FF6B35]" data-testid="ambassador-footer-link"><Flame className="h-4 w-4" /> Become an Ambassador</button>
          <button onClick={() => navigate("/faq")} className="flex items-center gap-2 hover:text-[#FF6B35]" data-testid="faq-link"><HelpCircle className="h-4 w-4" /> FAQ</button>
        </div>
        <footer className="py-6 text-center text-xs text-slate-400">© 2026 ClassMate — Medical Education Platform</footer>
      </main>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white dark:bg-[#0B1524] border-t border-slate-200 dark:border-slate-700" data-testid="mobile-bottom-nav">
        <div className="grid grid-cols-5 h-16">
          {[
            { icon: Home, label: "Home", to: "/", tid: "nav-home", active: true },
            { icon: BookOpen, label: "My Courses", to: "/my-courses", tid: "nav-courses" },
            { icon: FileQuestion, label: "Practice", to: lastCourse?.id ? `/course/${lastCourse.id}` : "/home", tid: "nav-practice" },
            { icon: Trophy, label: "Champion", to: "/championship", tid: "nav-champion" },
            { icon: User, label: "Profile", to: "/home", tid: "nav-profile" },
          ].map((it) => (
            <button key={it.tid} onClick={() => navigate(it.to)} data-testid={it.tid}
                    className={`flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${it.active ? "text-[#2563EB]" : "text-slate-500"}`}>
              <it.icon className="h-5 w-5" />
              {it.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
